
import * as THREE from 'three';
import { DoubleSideSplineCurve } from './DoubleSideSplineCurve';
import ClipperLib from '../../../3dapps/libs/clipper_unminified';
import { SVGLoader2 } from './MySVGLoader';


function isEqual(a, b) {
    return Math.abs(a - b) < 1e-8;
}

function vector2ToFPoint(p) {
    return new ClipperLib.FPoint(p.x, p.y);
}

function fPointToVector2(p) {
    return new THREE.Vector2(p.X, p.Y);
}

function roundRectGetPoints(w, h, r, curveSegments) {
    let points = [];
    // 逆时针，从第一象限的圆弧开始，第四象限的圆弧结束
    let radEach = Math.PI * 0.5 / curveSegments;
    let cx = w - r;
    let cy = h - r;
    points.push(new THREE.Vector2(w, cy));
    for (let i = 1; i < curveSegments; i++) {
        let rad = i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(cx, h));

    cx = -(w - r);
    const PI_2 = Math.PI * 0.5;
    points.push(new THREE.Vector2(cx, h));
    for (let i = 1; i < curveSegments; i++) {
        let rad = PI_2 + i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(-w, cy));

    cy = -(h - r);
    const PI_2_2 = Math.PI;
    points.push(new THREE.Vector2(-w, cy));
    for (let i = 1; i < curveSegments; i++) {
        let rad = PI_2_2 + i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(cx, -h));

    cx = w - r;
    const PI_2_3 = Math.PI * 1.5;
    points.push(new THREE.Vector2(cx, -h));
    for (let i = 1; i < curveSegments; i++) {
        let rad = PI_2_3 + i * radEach;
        let x = cx + r * Math.cos(rad);
        let y = cy + r * Math.sin(rad);
        points.push(new THREE.Vector2(x, y));
    }
    points.push(new THREE.Vector2(w, cy));

    return points;
}

class RoundRectCurve {
    constructor(w, h, r, t, curveSegments) {
        this.width = w;
        this.height = h;
        this.radius = r;
        this.innerThickness = t;
        this.curveSegments = curveSegments;
        this.cx = this.width - this.radius;
        this.cy = this.height - this.radius;
        this.arcLen = this.radius * Math.PI * 0.5;
        this.stop1 = this.arcLen;
        this.stop2 = this.stop1 + this.cx * 2;
        this.stop3 = this.stop2 + this.arcLen;
        this.stop4 = this.stop3 + this.cy * 2;
        this.stop5 = this.stop4 + this.arcLen;
        this.stop6 = this.stop5 + this.cx * 2;
        this.stop7 = this.stop6 + this.arcLen;
    }

    getLength() {
        return (this.arcLen + this.cx + this.cy) * 4;
    }

    getUValues() {
        let uValues = [];
        let arcEach = this.arcLen / this.curveSegments;
        uValues.push(0);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(i * arcEach); }
        uValues.push(this.arcLen);

        let uBegin = this.arcLen + this.cx * 2;
        uValues.push(uBegin);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(uBegin + i * arcEach); }
        uBegin += this.arcLen;
        uValues.push(uBegin);

        uBegin += this.cy * 2;
        uValues.push(uBegin);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(uBegin + i * arcEach); }
        uBegin += this.arcLen;
        uValues.push(uBegin);

        uBegin += this.cx * 2;
        uValues.push(uBegin);
        for (let i = 1; i < this.curveSegments; i++) { uValues.push(uBegin + i * arcEach); }
        uBegin += this.arcLen;
        uValues.push(uBegin);

        uBegin += this.cy * 2;
        uValues.push(uBegin)

        return uValues;
    }

    getPointAtOuter(u) {
        let x, y;
        let cx = this.cx, cy = this.cy;
        if (u < this.stop1) {
            let rad = u / this.arcLen * Math.PI * 0.5;
            x = cx + this.radius * Math.cos(rad);
            y = cy + this.radius * Math.sin(rad);
        } else if (u < this.stop2) {
            u -= this.stop1;
            x = cx - u;
            y = this.height;
        } else if (u < this.stop3) {
            u -= this.stop2;
            let rad = Math.PI * 0.5 + u / this.arcLen * Math.PI * 0.5;
            x = -cx + this.radius * Math.cos(rad);
            y = cy + this.radius * Math.sin(rad);
        } else if (u < this.stop4) {
            u -= this.stop3;
            x = -this.width;
            y = cy - u;
        } else if (u < this.stop5) {
            u -= this.stop4;
            let rad = Math.PI + u / this.arcLen * Math.PI * 0.5;
            x = -cx + this.radius * Math.cos(rad);
            y = -cy + this.radius * Math.sin(rad);
        } else if (u < this.stop6) {
            u -= this.stop5;
            x = -cx + u;
            y = -this.height;
        } else if (u < this.stop7) {
            u -= this.stop6;
            let rad = Math.PI * 1.5 + u / this.arcLen * Math.PI * 0.5;
            x = cx + this.radius * Math.cos(rad);
            y = -cy + this.radius * Math.sin(rad);
        } else {
            u -= this.stop7;
            x = this.width;
            y = -cy + u;
        }
        return new THREE.Vector2(x, y);
    }

    getPointAtInner(u, innerThickness) {
        innerThickness = innerThickness || this.innerThickness;
        let x2, y2;
        let cx = this.cx, cy = this.cy;
        if (u < this.stop1) {
            let rad = u / this.arcLen * Math.PI * 0.5;
            x2 = cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = cy + (this.radius - innerThickness) * Math.sin(rad);
        } else if (u < this.stop2) {
            u -= this.stop1;
            x2 = cx - u;
            y2 = this.height - innerThickness;
        } else if (u < this.stop3) {
            u -= this.stop2;
            let rad = Math.PI * 0.5 + u / this.arcLen * Math.PI * 0.5;
            x2 = -cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = cy + (this.radius - innerThickness) * Math.sin(rad);
        } else if (u < this.stop4) {
            u -= this.stop3;
            x2 = -(this.width - innerThickness);
            y2 = cy - u;
        } else if (u < this.stop5) {
            u -= this.stop4;
            let rad = Math.PI + u / this.arcLen * Math.PI * 0.5;
            x2 = -cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = -cy + (this.radius - innerThickness) * Math.sin(rad);
        } else if (u < this.stop6) {
            u -= this.stop5;
            x2 = -cx + u;
            y2 = -(this.height - innerThickness);
        } else if (u < this.stop7) {
            u -= this.stop6;
            let rad = Math.PI * 1.5 + u / this.arcLen * Math.PI * 0.5;
            x2 = cx + (this.radius - innerThickness) * Math.cos(rad);
            y2 = -cy + (this.radius - innerThickness) * Math.sin(rad);
        } else {
            u -= this.stop7;
            x2 = this.width - innerThickness;
            y2 = -cy + u;
        }
        return new THREE.Vector2(x2, y2);
    }
}

export class PenholderMaker {
    thickness = 1; // 壁厚
    cutThickness = 0.5; // 切除部分厚度
    width = 40; // 宽度的一半
    height = 40; // 深度的一半
    depth = 120; // 高度
    borderRadius = 20; // 圆角半径，不能小于壁厚
    curveSegments = 60; // 圆弧分割段数
    padding = 1; // 上下边距
    // 底部类型
    // simple: 简易型，普通的实心
    // circle-holes: // 很多小孔
    // star-holes: // 很多星星
    // svg-holes: // 由svg文件提供
    bottomType = "simple";

    constructor() {
        // this.loadPathsFromSVG("/imgs/make_penholder/circleHole.svg").then(paths => {
        //     console.log("circleHole", paths);
        //     this.circleHole = paths[0][0]; // 要求只有一条路径
        // });
        // this.loadPathsFromSVG("/imgs/make_penholder/starHole.svg").then(paths => {
        //     console.log("circleHole", paths);
        //     this.starHole = paths[0][0]; // 要求只有一条路径
        // });
    }

    updateSize(widthFull, heightFull, depth, radiusRatio) {
        this.width = widthFull * 0.5;
        this.height = heightFull * 0.5;
        this.depth = depth;
        this.borderRadius = radiusRatio * Math.min(this.width,this.height);
        this.radius = radiusRatio * Math.min(this.width, this.height);
        this.curveSegments = Math.ceil(this.borderRadius * 3);
    }

    // 等长曲线，外侧
    getPoints() {
        return roundRectGetPoints(this.width, this.height, this.borderRadius, this.curveSegments);
    }

    // 等长曲线，内侧
    getPointsInner() {
        return roundRectGetPoints(this.width - this.thickness, this.height - this.thickness, this.borderRadius - this.thickness, this.curveSegments);
    }

    getPointsBetween() {
        return roundRectGetPoints(this.width - this.cutThickness, this.height - this.cutThickness, this.borderRadius - this.cutThickness, this.curveSegments);
    }

    getPRect() {
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments);
        let width = curve.getLength();
        let height = this.depth;
        return { width, height, padding: this.padding };
    }

    makeMesh(scene) {
        let geom = new THREE.BufferGeometry();
        geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(), 3));
        // let material = new THREE.MeshNormalMaterial({
        let material = new THREE.MeshPhongMaterial({
            // let material = new THREE.MeshLambertMaterial({
            // wireframe: true,
            flatShading: true,
        });
        this.mesh = new THREE.Mesh(geom, material);
        this.mesh.rotation.x = -Math.PI * 0.5;
        this.mesh.position.y = -this.depth * 0.5;
        this.mesh.frustumCulled = false;
        scene.add(this.mesh);
    }

    updateGeometry(vertices) {
        // let geom = new THREE.BufferGeometry();
        // geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertices), 3));
        // this.mesh.geometry.dispose();
        // console.time("merge vertices");
        // this.mesh.geometry = THREE.BufferGeometryUtils.mergeVertices(geom, 1e-9);
        // console.timeEnd("merge vertices");
        // this.mesh.geometry.computeVertexNormals();
        // console.log(vertices, this.mesh.geometry);

        this.mesh.geometry.attributes.position = new THREE.BufferAttribute(new Float32Array(vertices), 3);
        // this.mesh.geometry.computeVertexNormals();
    }

    // 添加底部：上下两个面
    addBottom(vertices, ps1, ps2) {
        if (this.bottomType === "simple") {
            // 底
            for (let i = 0, last = ps1[ps1.length - 1], curr = ps1[0]; i < ps1.length; i++, last = curr, curr = ps1[i]) {
                vertices.push(0, 0, 0, curr.x, curr.y, 0, last.x, last.y, 0);
            }
            // 内部底
            for (let i = 0, last = ps2[ps2.length - 1], curr = ps2[0], z = this.thickness; i < ps2.length; i++, last = curr, curr = ps2[i]) {
                vertices.push(0, 0, z, last.x, last.y, z, curr.x, curr.y, z);
            }
        } else {
            let holePatten;
            if (this.bottomType === "circle-holes") {
                holePatten = this.circleHole;
            } else if (this.bottomType === "star-holes") {
                holePatten = this.starHole;
            }
            let holePaths = [];
            const cubeSize = 8.6;
            const cubeSizeHalf = cubeSize * 0.5;
            let xRange = Math.ceil((this.width - cubeSizeHalf) / cubeSize);
            let yRange = Math.ceil((this.height - cubeSizeHalf) / cubeSize);
            console.log("circle-holes", xRange, yRange);
            // let origin = new THREE.Vector2(0, 0);
            for (let i = -xRange; i <= xRange; i++) {
                for (let j = -yRange; j <= yRange; j++) {
                    let x = i * cubeSize, y = j * cubeSize;
                    holePaths.push(holePatten.map(p => new ClipperLib.FPoint(p.x + x, p.y + y)));
                    // let rotate = Math.random() * Math.PI * 2;
                    // // let scale = Math.random() * 1.5 + 0.1;
                    // let scale = 1.0;
                    // let shift = new THREE.Vector2(
                    //     (Math.random() * cubeSize - cubeSizeHalf) * 0.5,
                    //     (Math.random() * cubeSize - cubeSizeHalf) * 0.5);
                    // holePaths.push(holePatten.map(p => {
                    //     let p2 = p.clone().rotateAround(origin, rotate).multiplyScalar(scale).add(shift);
                    //     // let p2 = p.clone();
                    //     // p2.rotateAround(origin, Math.random() * Math.PI * 2); //.multiplyScalar(Math.random() + 0.5);
                    //     return new ClipperLib.FPoint(p2.x + x, p2.y + y);
                    // }));
                }
            }
            // 用上表面与孔相交
            let cuttedHoles = new ClipperLib.Paths();
            let c = new ClipperLib.Clipper();
            let outerPath2 = [ps2.map(vector2ToFPoint)];
            c.AddPaths(outerPath2, ClipperLib.PolyType.ptSubject, true);
            c.AddPaths(holePaths, ClipperLib.PolyType.ptClip, true);
            c.Execute(ClipperLib.ClipType.ctIntersection, cuttedHoles);
            let solution2 = new ClipperLib.Paths();
            c.Execute(ClipperLib.ClipType.ctDifference, solution2);
            // 用下表面减去裁剪过的孔洞
            let solution1 = new ClipperLib.Paths();
            c = new ClipperLib.Clipper();
            let outerPath1 = [ps1.map(vector2ToFPoint)];
            c.AddPaths(outerPath1, ClipperLib.PolyType.ptSubject, true);
            c.AddPaths(cuttedHoles, ClipperLib.PolyType.ptClip, true);
            c.Execute(ClipperLib.ClipType.ctDifference, solution1);

            // 下表面：ps1 + holes
            // console.log(solution1, solution2);
            let path = {
                shape: solution1[0].map(fPointToVector2),
                holes: solution1.slice(1).map(hole => hole.map(fPointToVector2))
            };
            let faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
            let points = [...path.shape];
            path.holes.forEach(hole => { points.push(...hole); });
            faces.forEach(f => {
                let p1 = points[f[0]];
                let p2 = points[f[1]];
                let p3 = points[f[2]];
                vertices.push(p1.x, p1.y, 0);
                vertices.push(p3.x, p3.y, 0);
                vertices.push(p2.x, p2.y, 0);
            });
            // 上表面
            path = {
                shape: solution2[0].map(fPointToVector2),
                holes: solution2.slice(1).map(hole => hole.map(fPointToVector2))
            };
            faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
            points = [...path.shape];
            path.holes.forEach(hole => { points.push(...hole); });
            let z = this.thickness;
            faces.forEach(f => {
                let p1 = points[f[0]];
                let p2 = points[f[1]];
                let p3 = points[f[2]];
                vertices.push(p1.x, p1.y, z);
                vertices.push(p2.x, p2.y, z);
                vertices.push(p3.x, p3.y, z);
            });
            // 中间的壁
            cuttedHoles.forEach(hole => {
                for (let i = 0, last = hole[hole.length - 1], curr = hole[0]; i < hole.length; i++, last = curr, curr = hole[i]) {
                    vertices.push(last.X, last.Y, 0, curr.X, curr.Y, z, curr.X, curr.Y, 0);
                    vertices.push(last.X, last.Y, 0, last.X, last.Y, z, curr.X, curr.Y, z);
                }
            });
        }
    }

    // 素的，没有任何图案
    makePlain() {
        console.time("makePlain");
        let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let vertices = [];
        this.addBottom(vertices, ps1, ps2);
        // 外壁
        for (let i = 0, last = ps1[ps1.length - 1], z1 = 0, z2 = this.depth; i < ps1.length; i++) {
            let curr = ps1[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z1, curr.x, curr.y, z2);
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, last.x, last.y, z2);
            last = curr;
        }
        // 内壁
        for (let i = 0, last = ps2[ps2.length - 1], z1 = this.thickness, z2 = this.depth; i < ps2.length; i++) {
            let curr = ps2[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
            last = curr;
        }
        // 上边缘
        for (let i = 0, last1 = ps1[ps1.length - 1], last2 = ps2[ps2.length - 1], z = this.depth; i < ps1.length; i++) {
            let curr1 = ps1[i], curr2 = ps2[i];
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
            last1 = curr1; last2 = curr2;
        }

        this.updateGeometry(vertices);
        console.timeEnd("makePlain");
    }

    loadPathsFromSVG(svgFile) {
        return new Promise((resolve, reject) => {
            new SVGLoader2().load(svgFile, function (data) {
                // let paths = data.paths.map(path => path.toShapes(true).map(shape => shape.extractPoints(12)));
                // let paths = data.paths.map(path => path.toShapes(true));
                // let paths = [];
                // console.log(data.paths, paths);
                let paths = [];
                data.paths.forEach(path => {
                    let shapes = path.toShapes(true);
                    shapes.forEach(shape => {
                        let shapePoints = shape.extractPoints(12);
                        shapePoints.shape.forEach(p => { p.y = -p.y; });
                        shapePoints.holes.forEach(hole => { hole.forEach(p => { p.y = -p.y; }) });
                        paths.push([shapePoints.shape, ...shapePoints.holes]);
                    });
                    // path.toShapes().forEach(shape => {
                    //     console.log("yyyy", shape);
                    //     console.log(shape.getPoints(3));
                    // });
                });
                // console.log(paths);
                // let pathList = [];
                // for (let i = 0; i < paths.length; i++) {
                //     let path = paths[i];
                //     let shapes = path.toShapes();
                //     for (let j = 0; j < shapes.length; j++) {
                //         let shape = shapes[j];
                //     }
                // }
                resolve(paths);
            }, null, reject);
        });
    }

    // 带浮雕。带padding。
    makeModelWithRelief(pathsList) {
        console.time("makeModelWithRelief");
        let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let vertices = [];
        this.addBottom(vertices, ps1, ps2);
        // 内壁
        for (let i = 0, last = ps2[ps2.length - 1], z1 = this.thickness, z2 = this.depth; i < ps2.length; i++) {
            let curr = ps2[i];
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
            last = curr;
        }
        // 上边缘
        for (let i = 0, last1 = ps1[ps1.length - 1], last2 = ps2[ps2.length - 1], z = this.depth; i < ps1.length; i++) {
            let curr1 = ps1[i], curr2 = ps2[i];
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
            last1 = curr1; last2 = curr2;
        }
        // 外壁：分割成一段一段的，再计算
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments); // 曲面
        let uList = curve.getUValues();
        let cutPathsList = pathsList.map(paths => paths.map(path => path.map(vector2ToFPoint)));

        // 上下边距，将其减去
        let cutPaths = new ClipperLib.Paths();
        let c = new ClipperLib.Clipper();
        let rect = [[new ClipperLib.FPoint(0, this.padding), new ClipperLib.FPoint(uList[uList.length - 1], this.padding), new ClipperLib.FPoint(uList[uList.length - 1], this.depth - this.padding), new ClipperLib.FPoint(0, this.depth - this.padding)]];
        c.AddPaths(rect, ClipperLib.PolyType.ptSubject, true);
        cutPathsList.forEach(cutPaths => { c.AddPaths(cutPaths, ClipperLib.PolyType.ptClip, true); });
        c.Execute(ClipperLib.ClipType.ctIntersection, cutPaths);

        for (let i = 1, uLast = uList[0], uCurr = uList[i], z1 = 0, z2 = this.depth, solution = new ClipperLib.PolyTree(); i < uList.length; i++, uLast = uCurr, uCurr = uList[i]) {
            // 相交
            if (uLast === uCurr) continue;
            let c = new ClipperLib.Clipper();
            let rect = [[new ClipperLib.FPoint(uLast, z1), new ClipperLib.FPoint(uCurr, z1), new ClipperLib.FPoint(uCurr, z2), new ClipperLib.FPoint(uLast, z2)]];
            c.AddPaths(rect, ClipperLib.PolyType.ptSubject, true);
            // cutPathsList.forEach(cutPaths => { c.AddPaths(cutPaths, ClipperLib.PolyType.ptClip, true); });
            c.AddPaths(cutPaths, ClipperLib.PolyType.ptClip, true);
            c.Execute(ClipperLib.ClipType.ctDifference, solution);
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                path.holes = node.Childs().map(holeNode => holeNode.Contour().map(fPointToVector2));
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [...path.shape];
                path.holes.forEach(hole => { points.push(...hole); });
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtOuter(u1.x);
                    let p2 = curve.getPointAtOuter(u2.x);
                    let p3 = curve.getPointAtOuter(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                });
                node.Childs().forEach(subNode => { nodes.push(...subNode.Childs()); });
            }

            c.Execute(ClipperLib.ClipType.ctIntersection, solution);
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                let nodeChilds = node.Childs();
                path.holes = nodeChilds.map(holeNode => holeNode.Contour().map(fPointToVector2));
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [...path.shape];
                path.holes.forEach(hole => { points.push(...hole); });
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtInner(u1.x);
                    let p2 = curve.getPointAtInner(u2.x);
                    let p3 = curve.getPointAtInner(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                });
                let bounds = [path.shape, ...path.holes];
                bounds.forEach(hole => {
                    for (let k = 0, last = hole[hole.length - 1], curr = hole[0]; k < hole.length; k++, last = curr, curr = hole[k]) {
                        if ((curr.x === uLast && last.x === uLast) || (curr.x === uCurr && last.x === uCurr)) continue;
                        let p1 = curve.getPointAtOuter(last.x);
                        let p2 = curve.getPointAtOuter(curr.x);
                        let p3 = curve.getPointAtInner(last.x);
                        let p4 = curve.getPointAtInner(curr.x);
                        vertices.push(p1.x, p1.y, last.y, p2.x, p2.y, curr.y, p4.x, p4.y, curr.y);
                        vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p3.x, p3.y, last.y);
                    }
                });
                nodeChilds.forEach(subNode => { nodes.push(...subNode.Childs()); });
            }
        }

        this.updateGeometry(vertices);
        console.timeEnd("makeModelWithRelief");
    }

    // 带浮雕。不带padding。
    makeModelWithRelief2(pathsList) {
        console.time("makeModelWithRelief");
        // let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let ps3 = this.getPointsBetween();
        let vertices = [];
        this.addBottom(vertices, ps3, ps2);
        // 内壁
        for (let i = 0, last = ps2[ps2.length - 1], curr = ps2[0], z1 = this.thickness, z2 = this.depth; i < ps2.length; i++, last = curr, curr = ps2[i]) {
            vertices.push(last.x, last.y, z1, curr.x, curr.y, z2, curr.x, curr.y, z1);
            vertices.push(last.x, last.y, z1, last.x, last.y, z2, curr.x, curr.y, z2);
        }
        // 上边缘
        for (let i = 0, last1 = ps3[ps3.length - 1], last2 = ps2[ps2.length - 1], curr1 = ps3[i], curr2 = ps2[i], z = this.depth; i < ps3.length; i++, last1 = curr1, last2 = curr2, curr1 = ps3[i], curr2 = ps2[i]) {
            vertices.push(last1.x, last1.y, z, curr1.x, curr1.y, z, curr2.x, curr2.y, z);
            vertices.push(last1.x, last1.y, z, curr2.x, curr2.y, z, last2.x, last2.y, z);
        }
        // 外壁：分割成一段一段的，再计算
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments); // 曲面
        let uList = curve.getUValues();
        let cutPathsList = pathsList.map(paths => paths.map(path => path.map(vector2ToFPoint)));

        // 上下边距，将其减去
        for (let i = 1, uLast = uList[0], uCurr = uList[i], z1 = 0, z2 = this.depth, solution = new ClipperLib.PolyTree(); i < uList.length; i++, uLast = uCurr, uCurr = uList[i]) {
            // 相交
            if (uLast === uCurr) continue;
            let c = new ClipperLib.Clipper();
            let rect = [[new ClipperLib.FPoint(uLast, z1), new ClipperLib.FPoint(uCurr, z1), new ClipperLib.FPoint(uCurr, z2), new ClipperLib.FPoint(uLast, z2)]];
            c.AddPaths(rect, ClipperLib.PolyType.ptSubject, true);
            cutPathsList.forEach(cutPaths => { c.AddPaths(cutPaths, ClipperLib.PolyType.ptClip, true); });
            c.Execute(ClipperLib.ClipType.ctDifference, solution);
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                path.holes = node.Childs().map(holeNode => holeNode.Contour().map(fPointToVector2));
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [...path.shape];
                path.holes.forEach(hole => { points.push(...hole); });
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtOuter(u1.x);
                    let p2 = curve.getPointAtOuter(u2.x);
                    let p3 = curve.getPointAtOuter(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                });
                let bounds = [path.shape, ...path.holes];
                bounds.forEach(hole => {
                    for (let k = 0, last = hole[hole.length - 1], curr = hole[0]; k < hole.length; k++, last = curr, curr = hole[k]) {
                        if ((curr.y === z1 && last.y === z1) || (curr.y === z2 && last.y === z2)) {
                            // if ((isEqual(curr.y, z1) && isEqual(last.y, z1)) || (isEqual(curr.y, z2) && isEqual(last.y, z2))) {
                            // console.log("sss12321321");
                            let p1 = curve.getPointAtOuter(last.x);
                            let p2 = curve.getPointAtOuter(curr.x);
                            let p3 = curve.getPointAtInner(last.x);
                            let p4 = curve.getPointAtInner(curr.x);
                            vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p2.x, p2.y, curr.y);
                            vertices.push(p1.x, p1.y, last.y, p3.x, p3.y, last.y, p4.x, p4.y, curr.y);
                        }
                    }
                });
                node.Childs().forEach(subNode => { nodes.push(...subNode.Childs()); });
            }

            c.Execute(ClipperLib.ClipType.ctIntersection, solution);
            for (let j = 0, nodes = solution.Childs(); j < nodes.length; j++) {
                let node = nodes[j];
                let path = { shape: node.Contour().map(fPointToVector2) };
                let nodeChilds = node.Childs();
                path.holes = nodeChilds.map(holeNode => holeNode.Contour().map(fPointToVector2));
                const faces = THREE.ShapeUtils.triangulateShape(path.shape, path.holes);
                let points = [...path.shape];
                path.holes.forEach(hole => { points.push(...hole); });
                faces.forEach(f => {
                    let u1 = points[f[0]];
                    let u2 = points[f[1]];
                    let u3 = points[f[2]];
                    let p1 = curve.getPointAtInner(u1.x);
                    let p2 = curve.getPointAtInner(u2.x);
                    let p3 = curve.getPointAtInner(u3.x);
                    vertices.push(p1.x, p1.y, u1.y);
                    vertices.push(p2.x, p2.y, u2.y);
                    vertices.push(p3.x, p3.y, u3.y);
                });
                let bounds = [path.shape, ...path.holes];
                bounds.forEach(hole => {
                    for (let k = 0, last = hole[hole.length - 1], curr = hole[0]; k < hole.length; k++, last = curr, curr = hole[k]) {
                        if ((curr.x === uLast && last.x === uLast) || (curr.x === uCurr && last.x === uCurr)
                            || (curr.y === 0 && last.y === 0) || (curr.y === this.depth && curr.y === this.depth)) continue;
                        let p1 = curve.getPointAtOuter(last.x);
                        let p2 = curve.getPointAtOuter(curr.x);
                        let p3 = curve.getPointAtInner(last.x);
                        let p4 = curve.getPointAtInner(curr.x);
                        vertices.push(p1.x, p1.y, last.y, p2.x, p2.y, curr.y, p4.x, p4.y, curr.y);
                        vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p3.x, p3.y, last.y);
                    }
                });
                nodeChilds.forEach(subNode => { nodes.push(...subNode.Childs()); });
            }
        }

        this.updateGeometry(vertices);
        console.timeEnd("makeModelWithRelief");
    }

    // 无浮雕。可以自定义上边界。
    makePlain2(yValues) {
        console.time("makePlain2");
        let curve = new RoundRectCurve(this.width, this.height, this.borderRadius, this.cutThickness, this.curveSegments); // 曲面
        let curveLen = curve.getLength();
        let topCurve = new DoubleSideSplineCurve(curveLen / yValues.length, yValues);
        let topPoints = topCurve.getPoints(160);
        topPoints.push(new THREE.Vector2(curveLen, 0), new THREE.Vector2(0, 0));
        // topPoints.reverse();
        console.log(topPoints);
        let ps1 = this.getPoints();
        let ps2 = this.getPointsInner();
        let vertices = [];
        this.addBottom(vertices, ps1, ps2);
        let uList = curve.getUValues();
        let topPath = [topPoints.map(vector2ToFPoint)];
        for (let i = 1, uLast = uList[0], uCurr = uList[1]; i < uList.length; i++, uLast = uCurr, uCurr = uList[i]) {
            if (uLast === uCurr) continue;
            let c = new ClipperLib.Clipper();
            let rectExpanded = [[new ClipperLib.FPoint(uLast, -1), new ClipperLib.FPoint(uCurr, -1), new ClipperLib.FPoint(uCurr, 1000), new ClipperLib.FPoint(uLast, 1000)]];
            let solution = new ClipperLib.Paths();
            c.AddPaths(topPath, ClipperLib.PolyType.ptSubject, true);;
            c.AddPaths(rectExpanded, ClipperLib.PolyType.ptClip, true);
            c.Execute(ClipperLib.ClipType.ctIntersection, solution);

            // console.log(solution);
            // if (solution.length === 0) continue;
            let pathShape = solution[0].map(fPointToVector2);
            const faces = THREE.ShapeUtils.triangulateShape(pathShape, []);
            faces.forEach(f => {
                let u1 = pathShape[f[0]];
                let u2 = pathShape[f[1]];
                let u3 = pathShape[f[2]];
                let p1 = curve.getPointAtOuter(u1.x);
                let p2 = curve.getPointAtOuter(u2.x);
                let p3 = curve.getPointAtOuter(u3.x);
                vertices.push(p1.x, p1.y, u1.y);
                vertices.push(p2.x, p2.y, u2.y);
                vertices.push(p3.x, p3.y, u3.y);
                p1 = curve.getPointAtInner(u1.x, this.thickness);
                p2 = curve.getPointAtInner(u2.x, this.thickness);
                p3 = curve.getPointAtInner(u3.x, this.thickness);
                vertices.push(p1.x, p1.y, u1.y);
                vertices.push(p3.x, p3.y, u3.y);
                vertices.push(p2.x, p2.y, u2.y);
            });
            for (let k = 0, last = pathShape[pathShape.length - 1], curr = pathShape[0]; k < pathShape.length; k++, last = curr, curr = pathShape[k]) {
                if ((curr.x === uLast && last.x === uLast) || (curr.x === uCurr && last.x === uCurr)) continue;
                let p1 = curve.getPointAtOuter(last.x);
                let p2 = curve.getPointAtOuter(curr.x);
                let p3 = curve.getPointAtInner(last.x, this.thickness);
                let p4 = curve.getPointAtInner(curr.x, this.thickness);
                vertices.push(p1.x, p1.y, last.y, p4.x, p4.y, curr.y, p2.x, p2.y, curr.y);
                vertices.push(p1.x, p1.y, last.y, p3.x, p3.y, last.y, p4.x, p4.y, curr.y);
            }
        }

        this.updateGeometry(vertices);
        console.timeEnd("makePlain2");
    }
}




// WEBPACK FOOTER //
// ./src/views/penholder/scripts/MakePenholder.js